import { ArrowRight, BarChart3, BookOpen, Boxes, BrainCircuit, Briefcase, Coffee, Flame, GraduationCap, Handshake, HeartHandshake, Megaphone, MessageCircle, Network, ShieldCheck, Smile, Sparkles, Target, type LucideIcon } from 'lucide-react'

export const CODEX_BASE_TEMPLATE_ID = 'codex-base'
export const FDE_PROJECT_LEAD_TEMPLATE_ID = 'fde-project-lead'
export const CODEX_DATA_SCIENTIST_TEMPLATE_ID = 'codex-data-scientist'
export const CODEX_PROJECT_MANAGER_TEMPLATE_ID = 'codex-project-manager'
export const CODEX_APPLIED_RESEARCHER_TEMPLATE_ID = 'codex-applied-researcher'
export const CODEX_BUSINESS_INVESTIGATOR_TEMPLATE_ID = 'codex-business-investigator'

export const PROFILE_LABELS: Record<string, string> = {
  'generalist': 'Analista de Operações',
  'ecommerce-marketing': 'Analista de Marketing',
  'customer-success': 'Customer Success',
  [CODEX_BASE_TEMPLATE_ID]: 'Base',
  [FDE_PROJECT_LEAD_TEMPLATE_ID]: 'Líder FDE',
  [CODEX_DATA_SCIENTIST_TEMPLATE_ID]: 'Data Scientist',
  [CODEX_PROJECT_MANAGER_TEMPLATE_ID]: 'Project Manager',
  [CODEX_APPLIED_RESEARCHER_TEMPLATE_ID]: 'Nikola · Applied AI Researcher',
  [CODEX_BUSINESS_INVESTIGATOR_TEMPLATE_ID]: 'Holmes · Business Investigator',
}

export const PROFILE_DESCRIPTIONS: Record<string, string> = {
  'generalist': 'Indicado para acompanhar rotinas, identificar gargalos e organizar prioridades operacionais.',
  'ecommerce-marketing': 'Indicado para acompanhar mídia, campanhas e oportunidades de crescimento em e-commerce.',
  'customer-success': 'Perfil reservado para supervisão interna da organização.',
  [CODEX_BASE_TEMPLATE_ID]: 'Base neutra para configurar comportamento, ferramentas e responsabilidades depois.',
  [FDE_PROJECT_LEAD_TEMPLATE_ID]: 'Conduz discovery, combina Workspace, memória e ontologia e mantém a continuidade dos projetos.',
  [CODEX_DATA_SCIENTIST_TEMPLATE_ID]: 'Investiga padrões e alavancas com análises reproduzíveis, sem assumir a ontologia ou a engenharia de dados.',
  [CODEX_PROJECT_MANAGER_TEMPLATE_ID]: 'Mantém compromissos, entregas e pendências atualizados, em colaboração com a FDE.',
  [CODEX_APPLIED_RESEARCHER_TEMPLATE_ID]: 'Pesquisa oportunidades para o cliente combinando ciência, tecnologia e soluções de mercado.',
  [CODEX_BUSINESS_INVESTIGATOR_TEMPLATE_ID]: 'Conduz investigações de valor com dados e contexto do projeto, delegando perguntas a subagentes.',
}

export const PROFILE_ICONS: Record<string, LucideIcon> = {
  'generalist': BarChart3,
  'ecommerce-marketing': Megaphone,
  'customer-success': HeartHandshake,
  [CODEX_BASE_TEMPLATE_ID]: Boxes,
  [FDE_PROJECT_LEAD_TEMPLATE_ID]: Network,
  [CODEX_DATA_SCIENTIST_TEMPLATE_ID]: BrainCircuit,
  [CODEX_PROJECT_MANAGER_TEMPLATE_ID]: Briefcase,
  [CODEX_APPLIED_RESEARCHER_TEMPLATE_ID]: GraduationCap,
  [CODEX_BUSINESS_INVESTIGATOR_TEMPLATE_ID]: Target,
}

export const SELECTABLE_PROFILE_IDS: readonly string[] = [
  'generalist',
  'ecommerce-marketing',
]

export const CODEX_TEMPLATE_IDS: readonly string[] = [
  CODEX_BASE_TEMPLATE_ID,
  FDE_PROJECT_LEAD_TEMPLATE_ID,
  CODEX_DATA_SCIENTIST_TEMPLATE_ID,
  CODEX_PROJECT_MANAGER_TEMPLATE_ID,
  CODEX_APPLIED_RESEARCHER_TEMPLATE_ID,
  CODEX_BUSINESS_INVESTIGATOR_TEMPLATE_ID,
]

export const DEFAULT_JOB_TITLE_BY_PROFILE: Record<string, string> = {
  'generalist': 'Analista de Operações',
  'ecommerce-marketing': 'Analista de Marketing',
  'customer-success': 'Customer Success',
  [CODEX_BASE_TEMPLATE_ID]: 'Assistente Generalista',
  [FDE_PROJECT_LEAD_TEMPLATE_ID]: 'FDE AI',
  [CODEX_DATA_SCIENTIST_TEMPLATE_ID]: 'Data Scientist',
  [CODEX_PROJECT_MANAGER_TEMPLATE_ID]: 'Project Manager',
  [CODEX_APPLIED_RESEARCHER_TEMPLATE_ID]: 'Applied AI Researcher',
  [CODEX_BUSINESS_INVESTIGATOR_TEMPLATE_ID]: 'Business Investigator',
}

export interface PersonalityOption {
  value: string
  description: string
  icon: LucideIcon
}

export interface PersonalityDimension {
  id: string
  label: string
  helper: string
  options: PersonalityOption[]
}

export const PERSONALITY_DIMENSIONS: PersonalityDimension[] = [
  {
    id: 'tom',
    label: 'Tom',
    helper: 'Como ele se expressa no dia a dia',
    options: [
      { value: 'Profissional', description: 'Linguagem formal e técnica', icon: Briefcase },
      { value: 'Equilibrado', description: 'Coloquial, mas educado', icon: MessageCircle },
      { value: 'Casual', description: 'Próximo e descontraído', icon: Coffee },
    ],
  },
  {
    id: 'brevidade',
    label: 'Brevidade',
    helper: 'O quanto cada resposta se estende',
    options: [
      { value: 'Conciso', description: 'Vai direto ao ponto', icon: ArrowRight },
      { value: 'Contextual', description: 'Contextualiza quando ajuda', icon: BookOpen },
      { value: 'Didático', description: 'Explica passo a passo', icon: GraduationCap },
    ],
  },
  {
    id: 'postura',
    label: 'Postura',
    helper: 'Como ele defende uma opinião',
    options: [
      { value: 'Diplomático', description: 'Suaviza divergências', icon: Handshake },
      { value: 'Direto', description: 'Diz o que pensa com franqueza', icon: Target },
      { value: 'Provocador', description: 'Desafia premissas', icon: Flame },
    ],
  },
  {
    id: 'humor',
    label: 'Humor',
    helper: 'O quanto ele aceita ser leve',
    options: [
      { value: 'Sério', description: 'Sem brincadeiras', icon: ShieldCheck },
      { value: 'Leve', description: 'Tom ameno e simpático', icon: Smile },
      { value: 'Espirituoso', description: 'Comentários afiados quando cabe', icon: Sparkles },
    ],
  },
]

export function parsePersonalityTraits(value: string | null | undefined): string[] {
  if (!value) return []
  return value
    .split(',')
    .map((trait) => trait.trim())
    .filter((trait) => trait.length > 0)
}

export function formatPersonalityTraits(traits: string[]): string {
  return traits.filter((trait) => trait.trim().length > 0).join(', ')
}

export function findDimensionForTrait(trait: string): PersonalityDimension | null {
  return PERSONALITY_DIMENSIONS.find((dimension) =>
    dimension.options.some((option) => option.value === trait),
  ) ?? null
}

export function jobTitleForFleetProfile(profile: string): string {
  return DEFAULT_JOB_TITLE_BY_PROFILE[profile] ?? 'Analista de Operações'
}

export function profileSelectable(profile: string): boolean {
  return /^custom:[0-9a-f-]{36}$/i.test(profile) || SELECTABLE_PROFILE_IDS.includes(profile) || CODEX_TEMPLATE_IDS.includes(profile)
}
